import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {
    ButtonWrapper,
    SelectDropdown,
    SelectOptionStyled,
    SelectStyled,
    SelectWrapper
} from "@/components/UI/Form/Selects/Select/Select.styled";
import Label from "@/components/UI/Form/Label/Label";
import ArrowDownIcon from "@/public/icons/arrow-down.svg";
import ErrorMessage from "@/components/UI/Form/ErrorMessage/ErrorMessage";
import {useTranslations} from "next-intl";
import Button from "@/components/UI/Button/Button";
import {Flex} from "antd";
import {CheckList, Popup, SearchBar} from "antd-mobile";
import {isMobile} from 'react-device-detect';
import {limitCallback} from "@/helpers/functions";

const SelectInput = ({
     options = [],
     selected,
     onChange,
     placeholder,
     showSearch,
     onSearch,
     disabled,
     error,
     suffixIcon = <ArrowDownIcon />,
     optionClassName,
     mode = null,
     label = null,
     withOptionAll = false,
     allOptionLabel = null,
     defaultValue = null,
     autoScrollToTheSelectedTime = false,
     autoSelectSingleOption = false,
     autoSelectFirstOption = false,
     ...props
 }) => {
    const innerPopupRef = useRef(null);
    const t = useTranslations('Fields')
    const [isDropdownOptionsOpen, setIsDropdownOptionsOpen] = useState(false)
    const [visible, setVisible] = useState(false)
    const isMultiple = mode === 'multiple'
    const searchBarRef = useRef(null);

    allOptionLabel ??= t('all')

    if (withOptionAll) {
        defaultValue = ['all']
        mode = 'multiple'
        options = [{value: 'all', label: allOptionLabel}, ...options]
    }

    const selectOptions = useMemo(() =>
        options.map((option) => ({
            value: option.value,
            label: (
                <SelectOptionStyled>
                    {option?.icon ?? ''} <span>{option.label}</span>
                </SelectOptionStyled>
            )
        })), [options]);

    useLayoutEffect(() => {
        if (!visible) {
            onSearch?.('');
        }
    }, [visible]);

    useEffect(() => {
        if (visible && isMobile && autoScrollToTheSelectedTime) {
            setTimeout(() => {
                const selectedItem = document.querySelector('.selected-checklist-item');
                if (selectedItem) {
                    selectedItem.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest'
                    });
                }
            }, 100);
        }
    }, [visible, isMobile, autoScrollToTheSelectedTime]);

    const handleChange = (value) => {
        if (!withOptionAll) {
            onChange?.(value);
            return;
        }
        if (value.at(-1) === 'all') {
            onChange?.(['all']);
            return;
        }
        if (value?.length === 0) {
            onChange?.(['all']);
            return;
        }
        onChange?.(value.filter(i => i !== 'all'));
    };

    const selectSingleOption = useCallback((singleOption) => {
        if (!isMultiple && selected !== singleOption) {
            onChange?.(singleOption);
            return;
        }

        if (isMultiple && selected.includes(singleOption)) {
            onChange?.([singleOption]);
        }
    }, [isMultiple, onChange, selected]);

    useEffect(() => {
        if (autoSelectSingleOption && selectOptions?.length === 1) {
            const singleOption = selectOptions[0].value;

            selectSingleOption(singleOption)
        }
    }, [autoSelectSingleOption, selectOptions, selectSingleOption])

    useEffect(() => {
        if (isMultiple && selected?.length > 0) return;

        if (selected) return;

        if (autoSelectFirstOption && selectOptions?.length > 0) {
            const singleOption = selectOptions[0].value;

            selectSingleOption(singleOption)
        }
    }, [autoSelectFirstOption, selectOptions, selectSingleOption, selected])

    const onScrollStart = useMemo(() => limitCallback(() => {
        searchBarRef.current?.blur()
    }), []);

    return (
        <>
            <SelectWrapper>
                {label && <Label>{label}</Label>}
                <SelectStyled
                    {...props}
                    mode={mode}
                    defaultValue={selected ?? defaultValue}
                    value={selected ?? defaultValue}
                    placeholder={placeholder}
                    showSearch={!isMobile && showSearch}
                    open={!isMobile && isDropdownOptionsOpen}
                    onDropdownVisibleChange={open => setIsDropdownOptionsOpen(open)}
                    onSearch={onSearch}
                    onChange={handleChange}
                    disabled={disabled}
                    options={Array.from(new Map(selectOptions.map(opt => [opt.value, opt])).values())}
                    suffixIcon={suffixIcon}
                    onClick={(e) => {
                        props.onClick?.(e);
                        if (isMobile) setVisible(true);
                    }}
                    dropdownRender={(dropdown) =>
                        <SelectDropdown
                            className={optionClassName}
                            variant={props.variant}
                            size={props.size}
                            ref={innerPopupRef}
                        >
                            {dropdown}
                            <Flex dir={'row'}>
                                {props.actions?.map(({ onClick, label }, index) => (
                                    <Button
                                        key={index}
                                        variant={'link'}
                                        size={'sm'}
                                        onClick={() => {
                                            onClick?.();
                                            setIsDropdownOptionsOpen(false);
                                        }}
                                    >
                                        {label}
                                    </Button>
                                ))}
                            </Flex>
                        </SelectDropdown>
                    }
                />
                <ErrorMessage error={error} />
            </SelectWrapper>

            <Popup
                visible={isMobile && visible && !disabled}
                onMaskClick={() => setVisible(false)}
                destroyOnClose
            >
                <div style={{paddingBottom: 10}}>
                    {
                        showSearch &&
                        <div style={{padding: 12}}>
                            <SearchBar
                                ref={searchBarRef}
                                placeholder={t('search_customer')}
                                onChange={v => onSearch?.(v)}
                                clearOnCancel={true}
                            />
                        </div>
                    }
                    <div ref={innerPopupRef}
                         style={{
                             maxHeight: '500px',
                             height: showSearch ? '500px' : 'fit-content',
                             overflowY: 'auto'
                         }}
                         onScroll={(e) => {
                             props.onPopupScroll?.(e)
                             onScrollStart(e)
                         }}
                    >
                        <CheckList
                            multiple={isMultiple}
                            value={Array.isArray(selected) ? selected : [selected]}
                            onChange={val => {
                                handleChange?.(isMultiple ? val : val[0]);
                                if (!isMultiple) setVisible(false);
                            }}
                        >
                            {selectOptions.map(item => {
                                const isSelected = Array.isArray(selected)
                                    ? selected.includes(item.value)
                                    : selected === item.value;

                                return (
                                    <CheckList.Item
                                        key={item.value}
                                        value={item.value}
                                        className={autoScrollToTheSelectedTime && isSelected ? 'selected-checklist-item' : ''}
                                    >
                                        {item.label}
                                    </CheckList.Item>
                                );
                            })}
                        </CheckList>
                    </div>
                    <ButtonWrapper>
                        {props.actions?.map(({ onClick, label }, index) => (
                            <Button
                                key={index}
                                variant={'link'}
                                size={'md'}
                                onClick={() => {
                                    onClick?.();
                                    setVisible(false);
                                }}
                            >
                                {label}
                            </Button>
                        ))}
                    </ButtonWrapper>
                </div>
            </Popup>
        </>
    );
};

export default SelectInput;
